import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { isPurchasable } from "../components/product/ProductPurchaseActions";
import { usePageMeta, SEO_CONSTANTS } from "../lib/usePageMeta";
import { useProductData } from "../hooks/useProductData";
import { ProductImages } from "../components/product/ProductImages";
import { ProductInfo } from "../components/product/ProductInfo";
import { ProductActions } from "../components/product/ProductActions";
import { ProductCOASection } from "../components/product/ProductCOASection";
import { ProductCard } from "../components/product/ProductCard";
import { trackViewItem } from "../lib/analytics";
import { api } from "../lib/api";
import { pickRelatedProducts, slugsInCart } from "../lib/product-recommendations";
import { recordProductView } from "../lib/recently-viewed";
import { useCart } from "../lib/cart";
import { formatPrice } from "../lib/format";
import { ShoppingCart } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * PDP orchestrator. Owns:
 *   • data-fetch via `useProductData(slug)`
 *   • per-route <head> metadata via `usePageMeta` (title, description,
 *     canonical, og:*, Product JSON-LD, BreadcrumbList JSON-LD)
 *   • layout container + breadcrumb strip + 2-column grid
 *
 * All rendering is delegated to focused subcomponents. **No** behavioral
 * change vs. the prior monolithic implementation — this is a purely
 * structural split.
 */
export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { product, notFound } = useProductData(slug);

  const absImage = (u) => {
    if (!u) return `${SEO_CONSTANTS.PROD_HOST}/og-default.png`;
    if (u.startsWith("http")) return u;
    return `${SEO_CONSTANTS.PROD_HOST}${u.startsWith("/") ? u : `/${u}`}`;
  };

  const canAdd = product ? isPurchasable(product) : false;
  const showPrice = !!(product && canAdd && product.public_price_visible !== false && product.retail_price_cents);

  // Ecommerce dataLayer — view_item fires once per PDP mount / slug change.
  // We also record a compact snapshot to first-party localStorage so a
  // "Recently viewed" rail can surface it without any server call.
  React.useEffect(() => {
    if (product) {
      trackViewItem(product);
      recordProductView(product);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Client-side "Frequently added together" recommendations — same
  // chemistry category as this product, in-stock, purchasable, cap 3,
  // no cart dupes.  Never implies a therapeutic combination.
  const { items: cartItems, add: cartAdd } = useCart();
  const [related, setRelated] = useState([]);
  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    api.get("/products", { params: { limit: 200 } })
      .then((r) => {
        if (cancelled) return;
        setRelated(pickRelatedProducts(product, r.data.items || [], slugsInCart(cartItems), 3));
      })
      .catch(() => setRelated([]));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, cartItems.length]);

  const handleStickyAdd = async () => {
    if (!product || !canAdd) return;
    try {
      await cartAdd(product, 1);
      toast.success(`${product.product_name} added to cart`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add to cart");
    }
  };

  usePageMeta({
    title: product
      // Owner-authoritative: "[Product Name] [Strength] | Medical Peptide Store"
      // — the display name already carries the strength, and usePageMeta
      // auto-appends " | Medical Peptide Store", so we pass just the
      // product name without any suffix.
      ? product.public_display_name
      : null,
    description: product
      ? (product.seo_description || product.short_description || "").trim()
      : null,
    canonicalPath: product ? `/product/${product.slug}` : null,
    ogType: "product",
    ogImage: product ? absImage(product.primary_image_url) : undefined,
    jsonLd: product ? buildProductJsonLd(product, canAdd, absImage) : [],
  });

  if (notFound) return <ProductNotFound slug={slug} />;
  if (!product) return <div className="mps-container py-32 text-brand-muted">Loading…</div>;

  return (
    <div className="bg-white" data-testid="product-detail-page">
      <div className="mps-container pt-8 pb-16">
        {/* Breadcrumbs */}
        <div className="text-[12px] font-medium text-brand-muted mb-8 flex items-center gap-2">
          <Link to="/shop" className="hover:text-brand-navy transition-colors">Catalog</Link>
          <span className="text-brand-border">›</span>
          <Link to={`/shop/category/${product.category}`} className="hover:text-brand-navy transition-colors">
            {product.research_category || product.category}
          </Link>
          <span className="text-brand-border">›</span>
          <span className="text-brand-navy font-semibold font-mono">{product.public_sku}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <ProductImages product={product} />

          {/* Info panel — sticky */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32 space-y-6">
              <ProductInfo product={product} canAdd={canAdd} />
              <ProductActions
                product={product}
                canAdd={canAdd}
                showPrice={showPrice}
                navigate={nav}
              />
            </div>
          </div>
        </div>

        <ProductCOASection product={product} />

        {/* Frequently added together — same category, in-stock, cap 3,
            honest.  Rendered only when at least one real recommendation exists. */}
        {related.length > 0 && (
          <section
            data-testid="pdp-related-section"
            className="mt-16 lg:mt-20 pt-12 border-t border-brand-border"
          >
            <div className="max-w-2xl mb-8">
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-accent mb-3">
                Frequently added together
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-brand-navy tracking-tight leading-tight">
                More research peptides in{" "}
                {product.research_category || product.category}
              </h2>
              <p className="mt-2 text-brand-muted text-sm leading-relaxed">
                Optional companions from the same research area, all in
                stock. Nothing here implies a therapy or protocol.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <div key={p.slug} data-testid={`pdp-related-card-${p.slug}`}>
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Mobile-only sticky Add-to-Cart bar. Hidden on lg+; the right rail
          carries the primary action on desktop. Reserves bottom safe-area. */}
      {canAdd && (
        <div
          data-testid="pdp-mobile-sticky-atc"
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-brand-border shadow-[0_-4px_16px_rgba(15,23,42,0.08)] pb-[env(safe-area-inset-bottom)]"
        >
          <div className="mps-container flex items-center gap-3 py-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-muted truncate">
                {product.public_display_name}
              </div>
              {showPrice && (
                <div className="font-display text-lg font-bold text-brand-navy leading-tight">
                  {formatPrice(product.retail_price_cents)}
                </div>
              )}
            </div>
            <button
              onClick={handleStickyAdd}
              data-testid="pdp-mobile-sticky-atc-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-[12px] bg-brand-accent hover:bg-[#1E6BD8] text-white font-semibold text-sm shrink-0 transition-[background-color] duration-200"
            >
              <ShoppingCart size={16} weight="regular" /> Add to Cart
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Product + BreadcrumbList JSON-LD. Pure function — kept in this file so the
 * behavior-critical schema stays visible to reviewers who audit the PDP for
 * SEO regressions.
 */
function buildProductJsonLd(product, canAdd, absImage) {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.public_display_name || product.product_name,
      "image": [absImage(product.primary_image_url), absImage(product.zoom_image_url)].filter(Boolean),
      "sku": product.public_sku,
      "mpn": product.public_sku,
      "description": product.seo_description || product.short_description || product.long_description?.slice(0, 500),
      "url": `${SEO_CONSTANTS.PROD_HOST}/product/${product.slug}`,
      "category": product.research_category || product.category,
      "brand": { "@type": "Brand", "name": "Medical Peptide Store" },
      ...(product.cas_number && product.cas_number !== "—"
        ? { "identifier": { "@type": "PropertyValue", "name": "CAS Number", "value": product.cas_number } }
        : {}),
      "offers": {
        "@type": "Offer",
        "url": `${SEO_CONSTANTS.PROD_HOST}/product/${product.slug}`,
        "priceCurrency": (product.currency || "usd").toUpperCase(),
        "price": (product.retail_price_cents / 100).toFixed(2),
        "priceValidUntil": new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().slice(0, 10),
        "availability":
          // Owner rule: mirror the actual purchasable state of the UI.  If the
          // shopper can click Add to Cart (canAdd) AND the product either has
          // inventory OR allows backorder, schema.org must say InStock.  We only
          // downgrade to OutOfStock when the product is NOT purchasable at all,
          // and to PreOrder when the product literally has a future-only ship
          // date recorded on the row.
          product.available_for_pre_order
            ? "https://schema.org/PreOrder"
            : canAdd &&
              ((product.inventory_quantity ?? 0) > 0 || product.allow_backorder)
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
        "itemCondition": "https://schema.org/NewCondition",
        "seller": { "@type": "Organization", "name": "Medical Peptide Store", "url": SEO_CONSTANTS.PROD_HOST },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SEO_CONSTANTS.PROD_HOST}/` },
        { "@type": "ListItem", "position": 2, "name": "Catalog", "item": `${SEO_CONSTANTS.PROD_HOST}/shop` },
        ...(product.research_category
          ? [{
              "@type": "ListItem",
              "position": 3,
              "name": product.research_category,
              "item": `${SEO_CONSTANTS.PROD_HOST}/shop/category/${product.category}`,
            }]
          : []),
        {
          "@type": "ListItem",
          "position": product.research_category ? 4 : 3,
          "name": product.public_display_name,
          "item": `${SEO_CONSTANTS.PROD_HOST}/product/${product.slug}`,
        },
      ],
    },
  ];
}

function ProductNotFound({ slug }) {
  usePageMeta({
    title: "Product not found",
    description: "We couldn’t find that research peptide in the catalog.",
    canonicalPath: `/product/${slug}`,
    robots: "noindex, nofollow",
  });
  return (
    <div className="mps-container py-32 text-center" data-testid="product-not-found" data-http-status="404">
      <h1 className="font-display text-3xl font-semibold text-brand-navy">Product not found</h1>
      <p className="mt-3 text-brand-muted text-sm">
        We couldn’t find that item. Return to the{" "}
        <Link to="/shop" className="text-brand-accent hover:underline">catalog</Link>{" "}
        or check the URL.
      </p>
    </div>
  );
}
